import { IntlShape } from "react-intl";

import { ConsigneeAddressType } from "pages/shipper/RequestFormV2/defaults/consigneeDefaults";
import { StopAddressType, StopTimeType, StopType } from "types/MultiStop";
import { getScheduleOptionResponseType } from "api/response/location.response";
import { stopAddressTypeToConsigneeString } from "@RequestFormV2/sections/Schedule/utils/mapStopToConsignee";

export type ScheduleOptionsConfig = getScheduleOptionResponseType["schedule_options"];
export type ScheduleTimeTypeOption = { label: string; value: StopTimeType };

export const multiStopPickupTimeTypeOptions = (intl: IntlShape) => [
  {
    label: intl.formatMessage({ id: "new-request.schedule.cargoReadyPickupOption.after", defaultMessage: "After" }),
    value: StopTimeType.AFTER,
  },
  {
    label: intl.formatMessage({
      id: "new-request.schedule.defaultCargoReadyDeliveryOption.between",
      defaultMessage: "Between",
    }),
    value: StopTimeType.BETWEEN,
  },
];

export const multiStopDeliveryTimeTypeOptions = (intl: IntlShape) => [
  {
    label: intl.formatMessage({
      id: "new-request.schedule.defaultCargoReadyDeliveryOption.before",
      defaultMessage: "Before",
    }),
    value: StopTimeType.BEFORE,
  },
  {
    label: intl.formatMessage({
      id: "new-request.schedule.defaultCargoReadyDeliveryOption.between",
      defaultMessage: "Between",
    }),
    value: StopTimeType.BETWEEN,
  },
];

export const asapDeliveryOption = (intl: IntlShape) => [
  {
    label: intl.formatMessage({
      id: "new-request.schedule.defaultCargoReadyDeliveryOption.asap",
      defaultMessage: "ASAP Delivery",
    }),
    value: StopTimeType.ASAP_DELIVERY,
  },
];

export const multiStopPickupDeliveryOption = (intl: IntlShape) => [
  {
    label: intl.formatMessage({ id: "general.address", defaultMessage: "Address" }),
    value: StopAddressType.ADDRESS,
  },
  {
    label: intl.formatMessage({ id: "general.airport", defaultMessage: "Airport" }),
    value: StopAddressType.AIRPORT,
  },
  {
    label: intl.formatMessage({ id: "general.port", defaultMessage: "Port" }),
    value: StopAddressType.SEAPORT,
  },
];

export function getAddressTypeOptions(
  intl: IntlShape,
  scheduleOptions?: ScheduleOptionsConfig,
  options?: { streetAddressOnly?: boolean },
) {
  if (options?.streetAddressOnly) {
    return multiStopPickupDeliveryOption(intl).filter((option) => option.value === StopAddressType.ADDRESS);
  }

  const showRegularAddress = scheduleOptions?.regular_address !== false;
  const showAirport = Boolean(scheduleOptions?.airport_selection);
  const showSeaport = Boolean(scheduleOptions?.seaport_selection);

  return multiStopPickupDeliveryOption(intl).filter((option) => {
    if (option.value === StopAddressType.ADDRESS) return showRegularAddress;
    if (option.value === StopAddressType.AIRPORT) return showAirport;
    if (option.value === StopAddressType.SEAPORT) return showSeaport;
    return false;
  });
}

export function getConsigneeAddressTypeOptions(intl: IntlShape, scheduleOptions?: ScheduleOptionsConfig) {
  return getAddressTypeOptions(intl, scheduleOptions).map((option) => ({
    label: option.label,
    value: stopAddressTypeToConsigneeString(option.value as StopAddressType) as ConsigneeAddressType,
  }));
}

export function getPickupTimeTypeOptions(intl: IntlShape) {
  return multiStopPickupTimeTypeOptions(intl);
}

export function getDeliveryTimeTypeOptions(intl: IntlShape, allowAsapDelivery: boolean) {
  return allowAsapDelivery
    ? [...multiStopDeliveryTimeTypeOptions(intl), ...asapDeliveryOption(intl)]
    : multiStopDeliveryTimeTypeOptions(intl);
}

export function getTimeTypeOptionsForStop(
  intl: IntlShape,
  stopType: StopType,
  scheduleOptions?: ScheduleOptionsConfig,
) {
  const allowAsapDelivery = Boolean(scheduleOptions?.allow_asap_delivery);
  return stopType === StopType.PICKUP
    ? getPickupTimeTypeOptions(intl)
    : getDeliveryTimeTypeOptions(intl, allowAsapDelivery);
}
